import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { AlertTriangle, BookOpenCheck, RefreshCw } from 'lucide-react';
import CpscExemptionRecords from './CpscExemptionRecords';
import CpscCertLinkRecords from './CpscCertLinkRecords';
import CpscTable, { CPSC_ROW_CLASS, CPSC_TH_CLASS } from './CpscTable';
import { STATUS as DRAFT_STATUS } from './CpscDrafts'; // ONE status vocabulary (§8.2) — labels shared with the Drafts engine

/**
 * CpscRegister — CHA-267 PR-3 (Ruling 4). The 3-way register: every issued/known compliance record in one
 * filterable place — CERTIFICATES (cpsc_drafts, incl. issued=certified), EXEMPTIONS, and CERT LINKS.
 * Pure client-side join over the three tenant-isolated collections the UI already reads — no new
 * collections, no callable changes.
 *
 * Row actions (Ruling 4) come from the SHARED record tables — CpscExemptionRecords (Revoke/evidence;
 * Edit deliberately absent here: the fabric form lives on the Exemptions surface, and gate #30 hides
 * Edit for declared records regardless) and CpscCertLinkRecords (Unlink + engine-resolved status) —
 * one implementation each, never a fork (§8.2). Certificates are DISPLAY-ONLY here: their lifecycle
 * actions (confirm/submit/certify/reopen/supersede) stay under Manage → Drafts, the one gated engine.
 */
const s = (v) => (v == null ? '' : String(v));

const TYPES = [
  { id: 'cert', label: 'Certificates' },
  { id: 'exemption', label: 'Exemptions' },
  { id: 'link', label: 'Cert links' },
];

export default function CpscRegister({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [typeFilter, setTypeFilter] = useState(() => new Set(['cert', 'exemption', 'link']));
  const [styleFilter, setStyleFilter] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftsError, setDraftsError] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDrafts = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true); setDraftsError(null);
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'cpsc_drafts', tenantId, 'drafts'));
      // archived = operator-deleted — not part of the register of records (self-review); everything else
      // (authoring + in-flight + terminal) stays visible and filterable.
      setDrafts(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => d.status !== 'archived'));
    } catch (err) {
      console.error('cpsc_drafts read failed', err);
      setDraftsError('Could not load certificates (check console).');
    } finally { setLoading(false); }
  }, [isAdmin, tenantId]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const toggleType = (id) => setTypeFilter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next.size ? next : prev; // at least one type stays selected
  });

  if (!isAdmin) return <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">The register is restricted to administrators.</div>;

  const filter = s(styleFilter).trim().toLowerCase();
  const visibleDrafts = drafts.filter((d) => {
    if (!filter) return true;
    const hay = `${s(d.style)} ${s(d.styleNo)} ${s(d.primaryProductId)} ${(Array.isArray(d.productList) ? d.productList.join(' ') : '')} ${s(d.reportNo)} ${s(d.draftId)}`.toLowerCase();
    return hay.includes(filter);
  });

  return (
    <div className="font-plex text-carbon-ink space-y-4">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink flex items-center gap-1.5"><BookOpenCheck size={16} /> CPSC — Register</h3>
        <p className="text-sm text-carbon-gray-70">Every certificate, exemption, and cert link in one place. Certificates are managed under <strong>Drafts</strong> (the gated lifecycle engine); exemption and link actions are available inline.</p>
      </div>

      {/* Filter bar — type chips + style search (client-side; the collections are already tenant-isolated reads) */}
      <div className="flex flex-wrap items-center gap-2 text-xs bg-carbon-white border border-carbon-border px-3 py-2" aria-label="register filters">
        {TYPES.map(({ id, label }) => (
          <button key={id} onClick={() => toggleType(id)} aria-label={`filter type ${id}`}
            aria-pressed={typeFilter.has(id)}
            className={`px-2 py-1 font-plex-mono text-[10.5px] uppercase tracking-[0.06em] border transition-colors ${typeFilter.has(id) ? 'bg-carbon-ink text-carbon-white border-carbon-ink' : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'}`}>
            {label}
          </button>
        ))}
        <input value={styleFilter} onChange={(e) => setStyleFilter(e.target.value)} placeholder="Filter by style / report / PID…"
          className="ml-auto w-64 border border-carbon-gray-30 bg-carbon-gray-10 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" aria-label="register style filter" />
        <button onClick={loadDrafts} disabled={loading} className="flex items-center gap-1 px-2 py-1 border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50" aria-label="refresh certificates">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Certificates (display-only — lifecycle lives under Drafts) */}
      {typeFilter.has('cert') && (
        <div className="bg-carbon-white border border-carbon-border p-3">
          <h4 className="font-plex-mono text-[10px] uppercase tracking-[0.12em] font-semibold text-carbon-gray-70 mb-2">Certificates ({visibleDrafts.length}{filter ? ` of ${drafts.length}` : ''})</h4>
          {draftsError && <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {draftsError}</div>}
          {visibleDrafts.length === 0 ? (
            <p className="text-xs text-carbon-gray-50">{filter ? 'No certificates match the filter.' : 'No certificate drafts yet.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <CpscTable
                className="w-full text-xs"
                headClassName={CPSC_TH_CLASS}
                columns={[
                  { key: 'report', label: 'Report No', className: 'py-1 pr-2 text-left' },
                  { key: 'style', label: 'Style / Products', className: 'py-1 pr-2 text-left' },
                  { key: 'type', label: 'Type', className: 'py-1 pr-2 text-left' },
                  { key: 'status', label: 'Status', className: 'py-1 pr-2 text-left' },
                  { key: 'source', label: 'Source', className: 'py-1 pr-2 text-left' },
                  { key: 'version', label: 'Version', className: 'py-1 pr-2 text-left' },
                ]}
              >
                {visibleDrafts.map((d) => (
                  <tr key={d.id} className={CPSC_ROW_CLASS}>
                    <td className="py-1 pr-2 font-plex-mono font-medium text-carbon-ink">{s(d.reportNo) || s(d.draftId)}</td>
                    <td className="py-1 pr-2 text-carbon-gray-70">{s(d.style) || s(d.styleNo) || (Array.isArray(d.productList) ? d.productList.slice(0, 4).join(', ') + (d.productList.length > 4 ? '…' : '') : s(d.primaryProductId))}</td>
                    <td className="py-1 pr-2">{s(d.certificateType) || '—'}</td>
                    <td className="py-1 pr-2">{(DRAFT_STATUS[s(d.status)] && DRAFT_STATUS[s(d.status)].label) || s(d.status) || '—'}</td>
                    <td className="py-1 pr-2">{d.source === 'MANUAL' ? 'Manual' : 'BV'}</td>
                    <td className="py-1 pr-2 font-plex-mono text-carbon-gray-50">{s(d.versionId) || '—'}</td>
                  </tr>
                ))}
              </CpscTable>
            </div>
          )}
        </div>
      )}

      {/* Exemptions + Links — the SHARED record tables with their actions + guards (Ruling 4, §8.2) */}
      {typeFilter.has('exemption') && <CpscExemptionRecords userProfile={userProfile} styleFilter={styleFilter} />}
      {typeFilter.has('link') && <CpscCertLinkRecords userProfile={userProfile} styleFilter={styleFilter} />}
    </div>
  );
}
